import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiSend, FiUpload, FiTrash2, FiDownload, FiX,
  FiImage, FiVideo, FiFile, FiChevronLeft, FiChevronRight,
  FiPaperclip, FiTool, FiUser, FiDollarSign, FiClock,
  FiCheckCircle, FiAlertTriangle, FiMessageSquare, FiEdit2,
} from "react-icons/fi";
import { maintenanceAPI, getFileUrl } from "../../services/api";

// ── Helpers ──────────────────────────────────────────────────────────────────
const isImage = (f) => f?.file_type?.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(f?.file_name||"");
const isVideo = (f) => f?.file_type?.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(f?.file_name||"");
const fmt = (b) => { if(!b) return ""; if(b<1024) return `${b} B`; if(b<1048576) return `${(b/1024).toFixed(0)} KB`; return `${(b/1048576).toFixed(1)} MB`; };
const fmtMoney = (v) => v ? `${parseInt(v).toLocaleString("fr-FR")} XOF` : "-";
const fmtDate = (d) => d ? new Date(d).toLocaleString("fr-FR") : "-";

const STATUS_CFG = {
  open:        { label:"Ouvert",    bg:"#fef3c7", color:"#92400e", dot:"#f59e0b", Icon:FiClock },
  in_progress: { label:"En cours",  bg:"#dbeafe", color:"#1e40af", dot:"#3b82f6", Icon:FiTool },
  completed:   { label:"Terminé",   bg:"#d1fae5", color:"#065f46", dot:"#10b981", Icon:FiCheckCircle },
  closed:      { label:"Fermé",     bg:"#f1f5f9", color:"#475569", dot:"#94a3b8", Icon:FiX },
};
const PRIO_CFG = {
  urgent: { label:"Urgente", cls:"badge-danger" },
  high:   { label:"Haute",   cls:"badge-warning" },
  medium: { label:"Moyenne", cls:"badge-info" },
  low:    { label:"Basse",   cls:"badge-secondary" },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status]||STATUS_CFG.open;
  return <span style={{ display:"inline-flex",alignItems:"center",gap:6,padding:"4px 12px",borderRadius:20,background:c.bg,color:c.color,fontWeight:600,fontSize:13 }}><span style={{ width:8,height:8,borderRadius:"50%",background:c.dot,flexShrink:0 }}/>{c.label}</span>;
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i=>(i-1+images.length)%images.length);
  const next = () => setIdx(i=>(i+1)%images.length);
  useEffect(()=>{ const h=(e)=>{ if(e.key==="Escape")onClose(); if(e.key==="ArrowLeft")prev(); if(e.key==="ArrowRight")next(); }; window.addEventListener("keydown",h); return ()=>window.removeEventListener("keydown",h); });
  const cur = images[idx];
  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.93)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center" }}>
      <button onClick={onClose} style={{ position:"absolute",top:16,right:20,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:"50%",width:40,height:40,fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiX/></button>
      {images.length>1&&<>
        <button onClick={e=>{e.stopPropagation();prev();}} style={{ position:"absolute",left:16,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:"50%",width:44,height:44,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiChevronLeft/></button>
        <button onClick={e=>{e.stopPropagation();next();}} style={{ position:"absolute",right:16,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:"50%",width:44,height:44,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiChevronRight/></button>
      </>}
      <img src={getFileUrl(cur.file_url)||cur.file_url} alt={cur.file_name} onClick={e=>e.stopPropagation()} style={{ maxWidth:"90vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8,boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }}/>
      <div style={{ color:"rgba(255,255,255,0.7)",marginTop:12,fontSize:13,textAlign:"center" }}>{cur.file_name} {images.length>1&&`(${idx+1}/${images.length})`}</div>
      <a href={getFileUrl(cur.file_url)||cur.file_url} target="_blank" rel="noreferrer" download style={{ marginTop:8,color:"rgba(255,255,255,0.6)",fontSize:12,display:"inline-flex",alignItems:"center",gap:4,textDecoration:"none" }}><FiDownload size={14}/> Télécharger</a>
    </div>
  );
};

// ── DropZone ──────────────────────────────────────────────────────────────────
const DropZone = ({ files, onChange, maxFiles=8 }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = React.useRef();
  const addFiles = (newFiles) => { const combined=[...files]; for(const f of newFiles){ if(combined.length>=maxFiles)break; if(!combined.find(x=>x.name===f.name&&x.size===f.size))combined.push(f); } onChange(combined); };
  const removeFile = (i) => onChange(files.filter((_,ix)=>ix!==i));
  return (
    <div>
      <div onDragOver={e=>{e.preventDefault();setDragging(true);}} onDragLeave={()=>setDragging(false)}
        onDrop={e=>{e.preventDefault();setDragging(false);addFiles(Array.from(e.dataTransfer.files));}}
        onClick={()=>inputRef.current?.click()}
        style={{ border:`2px dashed ${dragging?"var(--primary)":"var(--border)"}`,borderRadius:10,padding:"16px",textAlign:"center",cursor:"pointer",background:dragging?"rgba(124,58,237,0.05)":"var(--bg-secondary)",transition:"all 0.2s" }}>
        <FiUpload size={24} style={{ color:dragging?"var(--primary)":"var(--text-secondary)",marginBottom:6 }}/>
        <div style={{ fontSize:13,fontWeight:600,color:dragging?"var(--primary)":"var(--text-primary)" }}>Glissez des fichiers ici</div>
        <div style={{ fontSize:11,color:"var(--text-secondary)",marginTop:3 }}>Images, Vidéos, PDF — Max {maxFiles} fichiers — 100 MB par fichier</div>
        <input ref={inputRef} type="file" multiple accept="image/*,video/*,application/pdf" style={{ display:"none" }} onChange={e=>addFiles(Array.from(e.target.files))}/>
      </div>
      {files.length>0&&(
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px,1fr))",gap:8,marginTop:10 }}>
          {files.map((file,i)=>{
            const isImg=file.type.startsWith("image/");
            const isVid=file.type.startsWith("video/");
            const prev=isImg?URL.createObjectURL(file):null;
            return (
              <div key={i} style={{ position:"relative",borderRadius:8,overflow:"hidden",background:"var(--bg-secondary)",border:"1px solid var(--border)",aspectRatio:"1",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column" }}>
                {isImg&&prev?<img src={prev} alt={file.name} style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                  :<div style={{ textAlign:"center",padding:6 }}>{isVid?<FiVideo size={20} color="#7c3aed"/>:<FiFile size={20} color="#64748b"/>}<div style={{ fontSize:10,marginTop:3,color:"var(--text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:70 }}>{file.name}</div><div style={{ fontSize:10,color:"var(--text-secondary)" }}>{fmt(file.size)}</div></div>}
                <button type="button" onClick={e=>{e.stopPropagation();removeFile(i);}} style={{ position:"absolute",top:3,right:3,background:"rgba(220,38,38,0.85)",color:"#fff",border:"none",borderRadius:"50%",width:20,height:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiX size={10}/></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── MediaGallery ──────────────────────────────────────────────────────────────
const MediaGallery = ({ attachments, onDelete }) => {
  const [lbIdx, setLbIdx] = useState(null);
  const images = attachments.filter(isImage);
  const videos = attachments.filter(isVideo);
  const docs   = attachments.filter(a=>!isImage(a)&&!isVideo(a));
  if (attachments.length===0) return <div style={{ textAlign:"center",padding:"24px 0",color:"var(--text-secondary)" }}><FiPaperclip size={32} style={{ marginBottom:8,opacity:0.4 }}/><div style={{ fontSize:13 }}>Aucune pièce jointe</div></div>;
  return (
    <div>
      {images.length>0&&(
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:10,display:"flex",alignItems:"center",gap:6 }}><FiImage size={12}/>Photos ({images.length})</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px,1fr))",gap:8 }}>
            {images.map((att,i)=>(
              <div key={att.id} style={{ position:"relative",borderRadius:8,overflow:"hidden",aspectRatio:"1",cursor:"pointer",border:"1px solid var(--border)" }}>
                <img src={getFileUrl(att.file_url)||att.file_url} alt={att.file_name} style={{ width:"100%",height:"100%",objectFit:"cover" }} onClick={()=>setLbIdx(i)} />
                <div style={{ position:"absolute",bottom:0,left:0,right:0,background:"linear-gradient(transparent,rgba(0,0,0,0.6))",padding:"16px 4px 4px",display:"flex",justifyContent:"flex-end",gap:4 }}>
                  <button type="button" onClick={()=>setLbIdx(i)} style={{ background:"rgba(255,255,255,0.9)",border:"none",borderRadius:4,width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiImage size={11}/></button>
                  {onDelete&&<button type="button" onClick={()=>onDelete(att.id)} style={{ background:"rgba(220,38,38,0.9)",border:"none",borderRadius:4,color:"#fff",width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiTrash2 size={11}/></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {videos.length>0&&(
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:10,display:"flex",alignItems:"center",gap:6 }}><FiVideo size={12}/>Vidéos ({videos.length})</div>
          {videos.map(att=>(
            <div key={att.id} style={{ borderRadius:8,overflow:"hidden",border:"1px solid var(--border)",background:"#000",marginBottom:12,position:"relative" }}>
              <video src={getFileUrl(att.file_url)||att.file_url} controls style={{ width:"100%",maxHeight:240,display:"block" }} preload="metadata"/>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"var(--bg-secondary)",flexWrap:"wrap",gap:6 }}>
                <div>
                  <div style={{ fontSize:13,fontWeight:500 }}>{att.file_name}</div>
                  {att.file_size&&<div style={{ fontSize:11,color:"var(--text-secondary)" }}>{fmt(att.file_size)}</div>}
                </div>
                <div style={{ display:"flex",gap:8 }}>
                  <a href={getFileUrl(att.file_url)||att.file_url} target="_blank" rel="noreferrer" download style={{ color:"var(--primary)",display:"flex",alignItems:"center" }}><FiDownload size={16}/></a>
                  {onDelete&&<button type="button" onClick={()=>onDelete(att.id)} style={{ background:"none",border:"none",color:"#ef4444",cursor:"pointer",display:"flex",alignItems:"center" }}><FiTrash2 size={16}/></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {docs.length>0&&(
        <div>
          <div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:10,display:"flex",alignItems:"center",gap:6 }}><FiFile size={12}/>Documents ({docs.length})</div>
          {docs.map(att=>(
            <div key={att.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"var(--bg-secondary)",borderRadius:8,border:"1px solid var(--border)",marginBottom:8 }}>
              <FiFile size={22} color="#7c3aed" style={{ flexShrink:0 }}/>
              <div style={{ flex:1,overflow:"hidden" }}>
                <div style={{ fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{att.file_name}</div>
                {att.file_size&&<div style={{ fontSize:11,color:"var(--text-secondary)" }}>{fmt(att.file_size)}</div>}
              </div>
              <a href={getFileUrl(att.file_url)||att.file_url} target="_blank" rel="noreferrer" style={{ color:"var(--primary)",display:"flex",alignItems:"center",gap:4,fontSize:13 }}><FiDownload size={15}/></a>
              {onDelete&&<button type="button" onClick={()=>onDelete(att.id)} style={{ background:"none",border:"none",color:"#ef4444",cursor:"pointer" }}><FiTrash2 size={16}/></button>}
            </div>
          ))}
        </div>
      )}
      {lbIdx!==null&&<Lightbox images={images} startIndex={lbIdx} onClose={()=>setLbIdx(null)}/>}
    </div>
  );
};

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, count }) => (
  <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:14,paddingBottom:10,borderBottom:"1px solid var(--border)" }}>
    <div style={{ width:30,height:30,borderRadius:8,background:"rgba(124,58,237,0.1)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
      <Icon size={15} color="#7c3aed"/>
    </div>
    <h2 style={{ margin:0,fontSize:15,fontWeight:700 }}>{title}</h2>
    {count!=null&&<span style={{ background:"var(--bg-secondary)",color:"var(--text-secondary)",borderRadius:20,fontSize:12,padding:"1px 8px",fontWeight:600 }}>{count}</span>}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const MaintenanceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editCost, setEditCost] = useState(false);
  const [costData, setCostData] = useState({ estimated_cost:"", actual_cost:"" });

  const fetchTicket = async () => {
    try {
      const r = await maintenanceAPI.getById(id);
      setTicket(r.data);
      setCostData({ estimated_cost: r.data.estimated_cost||"", actual_cost: r.data.actual_cost||"" });
    } catch { navigate("/maintenance"); }
    finally { setLoading(false); }
  };
  useEffect(()=>{ fetchTicket(); },[id]); // eslint-disable-line

  const handleStatusChange = async (status) => {
    await maintenanceAPI.updateStatus(id, { status });
    fetchTicket();
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await maintenanceAPI.addComment(id, { content: comment, is_internal: isInternal });
    setComment(""); fetchTicket();
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      uploadFiles.forEach(f=>fd.append("files",f));
      await maintenanceAPI.uploadAttachment(id, fd);
      setUploadFiles([]); setShowUpload(false);
      fetchTicket();
    } catch(e){ alert(e.response?.data?.error||"Erreur upload"); }
    finally { setUploading(false); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Supprimer définitivement cette pièce jointe ?")) return;
    await maintenanceAPI.deleteAttachment(id, attachmentId);
    fetchTicket();
  };

  const handleSaveCosts = async () => {
    await maintenanceAPI.update(id, { estimated_cost: costData.estimated_cost||null, actual_cost: costData.actual_cost||null });
    setEditCost(false); fetchTicket();
  };

  if (loading) return <div className="loading"><div className="spinner"/></div>;
  if (!ticket) return null;

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
          <button className="btn btn-secondary btn-sm" onClick={()=>navigate("/maintenance")}><FiArrowLeft/> Retour</button>
          <div>
            <div style={{ fontSize:12,color:"var(--text-secondary)",marginBottom:2 }}>{ticket.ticket_number}</div>
            <h1 style={{ margin:0,fontSize:18 }}>{ticket.title}</h1>
          </div>
        </div>
        <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
          <StatusBadge status={ticket.status}/>
          <span className={`badge ${PRIO_CFG[ticket.priority]?.cls||"badge-info"}`}>{PRIO_CFG[ticket.priority]?.label||ticket.priority}</span>
          {ticket.status==="open"&&<button className="btn btn-warning btn-sm" onClick={()=>handleStatusChange("in_progress")}>Prendre en charge</button>}
          {ticket.status==="in_progress"&&<button className="btn btn-success btn-sm" onClick={()=>handleStatusChange("completed")}>Marquer terminé</button>}
          {ticket.status==="completed"&&<button className="btn btn-secondary btn-sm" onClick={()=>handleStatusChange("closed")}>Clôturer</button>}
        </div>
      </div>

      {/* ── Main Grid Responsive ── */}
      <div className="maintenance-detail-grid">
        {/* ─ Colonne gauche ─ */}
        <div style={{ display:"grid",gap:20 }}>
          {/* Détails */}
          <div className="card">
            <div className="card-body">
              <SectionHeader icon={FiTool} title="Détails du ticket"/>
              <div className="responsive-grid-2">
                <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Bien</div>
                  <div style={{ fontSize:14,fontWeight:500 }}>{ticket.property_title||"-"}</div>
                  {ticket.property_address&&<div style={{ fontSize:12,color:"var(--text-secondary)" }}>{ticket.property_address}</div>}
                </div>
                <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Catégorie</div>
                  <div style={{ fontSize:14,fontWeight:500 }}>{ticket.category_name||"-"}</div>
                </div>
                <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Date de déclaration</div>
                  <div style={{ fontSize:14 }}>{fmtDate(ticket.created_at)}</div>
                </div>
                {ticket.completed_date&&<div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Date de résolution</div>
                  <div style={{ fontSize:14 }}>{fmtDate(ticket.completed_date)}</div>
                </div>}
                <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Prestataire</div>
                  <div style={{ fontSize:14 }}>{ticket.provider_name||"-"}</div>
                </div>
                <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"10px 14px" }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:3 }}>Assigné à</div>
                  <div style={{ fontSize:14 }}>{ticket.assigned_first_name ? `${ticket.assigned_first_name} ${ticket.assigned_last_name}` : "-"}</div>
                </div>
              </div>
              {ticket.description&&(
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:8 }}>Description</div>
                  <div style={{ fontSize:14,lineHeight:1.7,whiteSpace:"pre-wrap",background:"var(--bg-secondary)",padding:"12px 14px",borderRadius:8,border:"1px solid var(--border)" }}>{ticket.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* Coûts */}
          <div className="card">
            <div className="card-body">
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
                <SectionHeader icon={FiDollarSign} title="Suivi des coûts"/>
                <button type="button" onClick={()=>setEditCost(v=>!v)} className="btn btn-secondary btn-sm" style={{ display:"flex",alignItems:"center",gap:4 }}><FiEdit2 size={13}/>{editCost?"Annuler":"Modifier"}</button>
              </div>
              {editCost ? (
                <div className="responsive-grid-2">
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Coût estimé (XOF)</label>
                    <input type="number" className="form-input" value={costData.estimated_cost} onChange={e=>setCostData(d=>({...d,estimated_cost:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Coût réel (XOF)</label>
                    <input type="number" className="form-input" value={costData.actual_cost} onChange={e=>setCostData(d=>({...d,actual_cost:e.target.value}))} placeholder="0"/>
                  </div>
                  <div style={{ gridColumn:"1/-1",display:"flex",justifyContent:"flex-end" }}>
                    <button type="button" onClick={handleSaveCosts} className="btn btn-primary btn-sm">Enregistrer</button>
                  </div>
                </div>
              ) : (
                <div className="responsive-grid-2">
                  <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"12px 16px" }}>
                    <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:4 }}>Coût estimé</div>
                    <div style={{ fontSize:18,fontWeight:700,color:"var(--primary)" }}>{fmtMoney(ticket.estimated_cost)}</div>
                  </div>
                  <div style={{ background:"var(--bg-secondary)",borderRadius:8,padding:"12px 16px" }}>
                    <div style={{ fontSize:11,color:"var(--text-secondary)",fontWeight:600,textTransform:"uppercase",marginBottom:4 }}>Coût réel</div>
                    <div style={{ fontSize:18,fontWeight:700,color:ticket.actual_cost>ticket.estimated_cost?"#ef4444":"var(--text-primary)" }}>{fmtMoney(ticket.actual_cost)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Commentaires */}
          <div className="card">
            <div className="card-body">
              <SectionHeader icon={FiMessageSquare} title="Commentaires" count={ticket.comments?.length}/>
              <div style={{ display:"grid",gap:10,marginBottom:16 }}>
                {ticket.comments?.length===0 && <p style={{ color:"var(--text-secondary)",fontStyle:"italic",fontSize:13 }}>Aucun commentaire pour le moment.</p>}
                {ticket.comments?.map(c=>(
                  <div key={c.id} style={{ padding:"10px 14px",background:"var(--bg-secondary)",borderRadius:8,borderLeft:`3px solid ${c.is_internal?"var(--warning)":"var(--primary)"}` }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:4 }}>
                      <div style={{ fontSize:13,fontWeight:600 }}>{c.first_name} {c.last_name}</div>
                      <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                        {c.is_internal&&<span className="badge badge-warning" style={{ fontSize:10 }}>Interne</span>}
                        <span style={{ fontSize:11,color:"var(--text-secondary)" }}>{fmtDate(c.created_at)}</span>
                      </div>
                    </div>
                    <div style={{ fontSize:14,lineHeight:1.5 }}>{c.content}</div>
                  </div>
                ))}
              </div>
              <form onSubmit={handleAddComment}>
                <div style={{ marginBottom:8 }}>
                  <textarea className="form-control" placeholder="Ajouter un commentaire ou une note..." value={comment} onChange={e=>setComment(e.target.value)} rows={3} style={{ resize:"vertical" }}/>
                </div>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8 }}>
                  <label style={{ display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13 }}>
                    <input type="checkbox" checked={isInternal} onChange={e=>setIsInternal(e.target.checked)}/>
                    <span style={{ color:"var(--text-secondary)" }}>Note interne (invisible au locataire)</span>
                  </label>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!comment.trim()} style={{ display:"flex",alignItems:"center",gap:6 }}><FiSend size={14}/> Envoyer</button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ─ Colonne droite ─ */}
        <div style={{ display:"grid",gap:20 }}>
          {/* Locataire */}
          <div className="card">
            <div className="card-body">
              <SectionHeader icon={FiUser} title="Locataire"/>
              {ticket.tenant_first_name ? (
                <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                  <div style={{ width:44,height:44,borderRadius:"50%",background:"rgba(124,58,237,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:"var(--primary)",flexShrink:0 }}>
                    {ticket.tenant_first_name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontWeight:600,fontSize:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{ticket.tenant_first_name} {ticket.tenant_last_name}</div>
                    {ticket.tenant_email&&<div style={{ fontSize:12,color:"var(--text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{ticket.tenant_email}</div>}
                    {ticket.tenant_phone&&<div style={{ fontSize:12,color:"var(--text-secondary)" }}>{ticket.tenant_phone}</div>}
                  </div>
                </div>
              ) : <p style={{ color:"var(--text-secondary)",fontSize:13 }}>Locataire non spécifié</p>}
            </div>
          </div>

          {/* Statut rapide */}
          <div className="card">
            <div className="card-body">
              <SectionHeader icon={FiClock} title="Avancement"/>
              {["open","in_progress","completed","closed"].map((s,i)=>{
                const c=STATUS_CFG[s];
                const done=["open","in_progress","completed","closed"].indexOf(ticket.status)>=i;
                const current=ticket.status===s;
                return (
                  <div key={s} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:i<3?12:0 }}>
                    <div style={{ width:12,height:12,borderRadius:"50%",background:done?c.dot:"var(--border)",flexShrink:0,border:current?`2px solid ${c.dot}`:"none",transition:"all 0.3s" }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13,fontWeight:current?700:500,color:done?"var(--text-primary)":"var(--text-secondary)" }}>{c.label}</div>
                    </div>
                    {current&&<c.Icon size={14} color={c.dot}/>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pièces jointes */}
          <div className="card">
            <div className="card-body">
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ width:30,height:30,borderRadius:8,background:"rgba(124,58,237,0.1)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><FiPaperclip size={15} color="#7c3aed"/></div>
                  <h2 style={{ margin:0,fontSize:15,fontWeight:700 }}>Pièces jointes</h2>
                  {ticket.attachments?.length>0&&<span style={{ background:"var(--bg-secondary)",color:"var(--text-secondary)",borderRadius:20,fontSize:12,padding:"1px 8px",fontWeight:600 }}>{ticket.attachments.length}</span>}
                </div>
                <button type="button" className="btn btn-primary btn-sm" onClick={()=>setShowUpload(v=>!v)} style={{ display:"flex",alignItems:"center",gap:4 }}><FiUpload size={13}/> Ajouter</button>
              </div>

              {showUpload&&(
                <div style={{ background:"var(--bg-secondary)",borderRadius:10,padding:14,marginBottom:14,border:"1px solid var(--border)" }}>
                  <DropZone files={uploadFiles} onChange={setUploadFiles}/>
                  {uploadFiles.length>0&&(
                    <div style={{ display:"flex",gap:10,marginTop:10 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={()=>{setUploadFiles([]);setShowUpload(false);}}>Annuler</button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleUpload} disabled={uploading}>{uploading?"Upload...":`Uploader (${uploadFiles.length})`}</button>
                    </div>
                  )}
                </div>
              )}

              <MediaGallery attachments={ticket.attachments||[]} onDelete={handleDeleteAttachment}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetailPage;
